import { TriangleAlert } from 'lucide-react';

import type { GetGapExecutionApiResponse } from '@momentum/types';

import { pluralize } from '../lib/format';
import { candidateFunnel, droppedCandidates } from '../lib/run-audit';

/**
 * The pre-merge layer: what became of every candidate both finder families
 * proposed.
 *
 * This is the only view of the merge stage. A gap's `sourceCandidateIds` point
 * here, and a candidate that never became a gap exists nowhere else — its
 * `dispositionReason` is the pipeline's only record of why a finder's proposal
 * was dropped.
 *
 * When nothing was dropped the section says so in the run's own numbers rather
 * than showing an invented example row. A fabricated card would be the one thing
 * on this page a reviewer could mistake for evidence.
 */
export function CandidateFunnel({
  execution,
}: {
  execution: GetGapExecutionApiResponse;
}) {
  const cells = candidateFunnel(execution);
  const dropped = droppedCandidates(execution);
  const [promoted, merged] = cells;

  return (
    <section>
      <div className="gr-rule gr-rule-flush">
        <span>Candidate funnel</span>
        <i />
      </div>

      <div className="gr-funnel">
        {cells.map((cell) => (
          <div
            className="gr-funnel-cell"
            key={cell.disposition}
            data-tone={cell.tone}
            data-empty={cell.count === 0}
          >
            <span className="gr-funnel-k">{cell.label}</span>
            <span className="gr-funnel-n">{cell.count}</span>
          </div>
        ))}
      </div>

      <div className="gr-stack gr-stack-top-lg">
        {dropped.length === 0 ? (
          <div className="gr-note">
            <TriangleAlert />
            <div>
              <strong>No candidate in this execution was ever dropped.</strong>{' '}
              {promoted?.count ?? 0} promoted, {merged?.count ?? 0} merged, zero
              filtered or discarded. A dropped candidate is the row this funnel
              exists for — it is the only carrier of{' '}
              <code>dispositionReason</code>, and so the only place the pipeline
              says why a finder&apos;s proposal did not become a gap. Nothing is
              shown here rather than a made-up example, because a fabricated
              candidate is the one thing on this page that could be mistaken for
              evidence.
            </div>
          </div>
        ) : (
          <>
            <p className="gr-cand-claim">
              {pluralize(dropped.length, 'candidate')} never reached
              adjudication.
            </p>
            {dropped.map(({ scopeName, candidate }) => (
              <div className="gr-cand" key={candidate.candidateId}>
                <div className="gr-cand-top">
                  <div>
                    <span className="gr-chip gr-chip-lg">
                      {candidate.candidateId}
                    </span>
                    <span className="gr-chip">{scopeName}</span>
                    <span className="gr-chip">
                      {candidate.detector} · {candidate.foundBy}
                    </span>
                  </div>
                  <span className="gr-chip" data-tone="rose">
                    {candidate.disposition}
                  </span>
                </div>
                <span className="gr-cand-k">dispositionReason</span>
                <p className="gr-cand-reason">
                  {candidate.dispositionReason === undefined
                    ? 'none recorded'
                    : `“${candidate.dispositionReason}”`}
                </p>
                <span className="gr-cand-k">claim</span>
                <p className="gr-cand-claim">{candidate.claim}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
